// =============================================
// QueueDashboard — Publication workflow: Articles → Images → WordPress
// =============================================

import React, { useState, useCallback, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Play, Pause, RotateCcw, Trash2, Rocket, Clock,
  ImageIcon, ExternalLink, CheckCircle2, Image as ImageLucide,
  Send, AlertCircle, Monitor, X, ChevronRight, ChevronDown, Terminal, Wrench,
  Calendar, Braces, Loader2,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { ProgressLogModal } from "./ProgressLogModal";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { LiveIndicator } from "@/components/ui/LiveIndicator";
import { useGenerationQueue } from "@/hooks/useGenerationQueue";
import { usePublishQueue } from "@/hooks/usePublishQueue";
import { useImageGeneration } from "@/hooks/useImageGeneration";
import { useThematicNodeContent } from "@/hooks/useThematicNodeContent";
import { useThematicNodes } from "@/hooks/useThematicNodes";
import { clearQueue } from "@/services/queueClient";
import {
  queueMap,
  processQueue,
  pauseQueue,
  retryFailed,
} from "@/services/batchOrchestratorService";
import { revertPublication } from "@/services/publishQueueService";
import { useSchemaBackfill } from "@/hooks/useSchemaBackfill";
import {
  fetchSchemaStatusMap,
  fetchWebsiteForBackfill,
  type WpCreds,
} from "@/services/schema/schemaBackfillService";
import { approveQaOverride } from "@/services/contentEngine/qaOverrideService";
import {
  clearBackdatedDates,
  fetchEffectivePublishDates,
} from "@/services/backdateScheduler";
import { BackdateScheduleDialog } from "./BackdateScheduleDialog";
import { useToast } from "@/hooks/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Undo2, AlertTriangle, ShieldCheck } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";

interface QueueDashboardProps {
  mapId: string;
  websiteId?: string | null;
}

const STATUS_COLORS: Record<string, string> = {
  queued: "bg-muted text-muted-foreground",
  in_progress: "bg-blue-500/15 text-blue-700 dark:text-blue-400",
  completed: "bg-green-500/15 text-green-700 dark:text-green-400",
  failed: "bg-red-500/15 text-red-700 dark:text-red-400",
  cancelled: "bg-muted text-muted-foreground/60",
  scheduled: "bg-amber-500/15 text-amber-700 dark:text-amber-400",
  publishing: "bg-blue-500/15 text-blue-700 dark:text-blue-400",
  published: "bg-green-500/15 text-green-700 dark:text-green-400",
  ready: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400",
};

const BROWSER_REQUIREMENT_DISMISSED_KEY = "queue_browser_requirement_dismissed";
// localStorage flag — czytany TEZ w publishQueueService.ts (browser-only mode).
// User toggle z UI w QueueDashboard. OPT-IN: default OFF, user musi zaznaczyc
// zeby generowac obrazki in-content.
export const ENABLE_IN_CONTENT_IMAGES_KEY = "enable_in_content_images";

export function QueueDashboard({ mapId, websiteId }: QueueDashboardProps) {
  const genQueue = useGenerationQueue(mapId);
  const pubQueue = usePublishQueue(mapId);
  const { contents } = useThematicNodeContent(mapId);
  const { nodes } = useThematicNodes(mapId);
  const imageGen = useImageGeneration(mapId);
  const [processing, setProcessing] = useState(false);
  const [browserAlertDismissed, setBrowserAlertDismissed] = useState(
    typeof window !== "undefined" &&
      window.localStorage.getItem(BROWSER_REQUIREMENT_DISMISSED_KEY) === "true",
  );
  const [enableInContentImages, setEnableInContentImages] = useState(
    typeof window !== "undefined" &&
      window.localStorage.getItem(ENABLE_IN_CONTENT_IMAGES_KEY) === "true",
  );
  const toggleEnableInContentImages = useCallback((checked: boolean) => {
    setEnableInContentImages(checked);
    if (typeof window !== "undefined") {
      if (checked) {
        window.localStorage.setItem(ENABLE_IN_CONTENT_IMAGES_KEY, "true");
      } else {
        window.localStorage.removeItem(ENABLE_IN_CONTENT_IMAGES_KEY);
      }
    }
  }, []);
  const [expandedQueueRows, setExpandedQueueRows] = useState<Set<string>>(new Set());
  const [progressModalQueueId, setProgressModalQueueId] = useState<string | null>(null);

  // QA Override dialog state — pozwala publikować artykuł z qa_status=needs_review/failed
  // (bypass gate publishQueueService:311). Po zatwierdzeniu node dostaje qa_status=approved + audit trail.
  // UWAGA: handleQaOverride używa toast w deps — definicja MUSI być po `const { toast } = useToast()` poniżej.
  const [qaOverrideTarget, setQaOverrideTarget] = useState<
    { nodeId: string; title: string; qaScore: number | null } | null
  >(null);
  const [qaOverrideReason, setQaOverrideReason] = useState("");
  const [qaOverrideLoading, setQaOverrideLoading] = useState(false);

  const [revertDialog, setRevertDialog] = useState<{ nodeId: string; title: string } | null>(null);
  const [revertClearImage, setRevertClearImage] = useState(true);
  const [reverting, setReverting] = useState(false);
  const [backdateDialogOpen, setBackdateDialogOpen] = useState(false);
  const [effectiveDates, setEffectiveDates] = useState<Map<string, Date | null>>(new Map());
  const { toast } = useToast();

  // Schema backfill (dogenerowanie schemy do opublikowanych) — per mapa/domena
  const schemaBackfill = useSchemaBackfill();
  const [backfillForce, setBackfillForce] = useState(false);
  const [websiteInfo, setWebsiteInfo] = useState<{ creds: WpCreds | null; schemaEnabled: boolean; name: string | null }>(
    { creds: null, schemaEnabled: false, name: null },
  );
  const [schemaStatus, setSchemaStatus] = useState<Map<string, string>>(new Map()); // nodeId → injected_at (ma schemę)

  // QA Override handlery — PO useToast (TDZ guard).
  const openQaOverride = useCallback(
    (nodeId: string, title: string, qaScore: number | null) => {
      setQaOverrideTarget({ nodeId, title, qaScore });
      setQaOverrideReason("");
    },
    [],
  );

  const handleQaOverride = useCallback(async () => {
    if (!qaOverrideTarget) return;
    setQaOverrideLoading(true);
    try {
      const result = await approveQaOverride(
        qaOverrideTarget.nodeId,
        qaOverrideReason,
      );
      if (result.success) {
        toast({
          title: "QA zatwierdzone",
          description: `"${qaOverrideTarget.title.slice(0, 50)}" — możesz teraz publikować.`,
        });
        setQaOverrideTarget(null);
        setQaOverrideReason("");
      } else {
        toast({
          title: "Błąd",
          description: result.error || "Nie udało się zatwierdzić QA",
          variant: "destructive",
        });
      }
    } finally {
      setQaOverrideLoading(false);
    }
  }, [qaOverrideTarget, qaOverrideReason, toast]);

  const handleRevert = useCallback(async () => {
    if (!revertDialog) return;
    setReverting(true);
    try {
      const res = await revertPublication(revertDialog.nodeId, { clearImage: revertClearImage });
      if (res.error) {
        toast({ title: "Błąd cofania", description: res.error, variant: "destructive" });
      } else {
        toast({
          title: "Publikacja cofnięta",
          description: `${res.wpTrashed ? "Post WP → draft (ukryty)" : "WP nietknięty (brak credentials lub błąd)"}, DB zresetowane. Node gotowy do ponownej publikacji.`,
        });
        setRevertDialog(null);
      }
    } catch (e) {
      toast({ title: "Błąd", description: e instanceof Error ? e.message : "Nieznany", variant: "destructive" });
    } finally {
      setReverting(false);
    }
  }, [revertDialog, revertClearImage, toast]);

  const toggleExpandQueueRow = useCallback((queueId: string) => {
    setExpandedQueueRows((prev) => {
      const next = new Set(prev);
      if (next.has(queueId)) next.delete(queueId); else next.add(queueId);
      return next;
    });
  }, []);

  const dismissBrowserAlert = useCallback(() => {
    setBrowserAlertDismissed(true);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(BROWSER_REQUIREMENT_DISMISSED_KEY, "true");
    }
  }, []);

  // Node title lookup
  const nodeTitleMap = new Map<string, string>();
  const nodeSlugMap = new Map<string, string>();
  for (const n of nodes) {
    nodeTitleMap.set(n.id, n.title || (n as any).canonical_question || "");
    if ((n as any).slug) nodeSlugMap.set(n.id, (n as any).slug);
  }

  // Categorize articles
  const readyArticles = contents.filter(
    (c) => c.final_html && (c.engine_stage === "ready" || c.qa_status === "approved") && c.engine_stage !== "published"
  );
  const publishedArticles = contents.filter((c) => c.engine_stage === "published");
  const nodesWithoutImage = readyArticles.filter((c) => !c.featured_image_url);
  const readyWithImage = readyArticles.filter((c) => !!c.featured_image_url);

  // Schema backfill targets: domyślnie tylko BEZ schemy; force = wszystkie opublikowane.
  const publishedKey = publishedArticles.map((c) => c.node_id).join(",");
  const backfillTargets = publishedArticles.filter(
    (c) => backfillForce || !schemaStatus.has(c.node_id),
  );
  const backfillN = backfillTargets.length;

  // Backdate scope = node_ids w kolejce ze statusem non-terminal (queued/scheduled)
  const backdateScopeNodeIds = pubQueue.items
    .filter((q) => q.node_id && (q.status === "queued" || q.status === "scheduled"))
    .map((q) => q.node_id as string);

  // Fetch effective_publish_date for visible queue items (backdated history preview).
  // MUSI być PO `readyArticles` declaration (TDZ).
  useEffect(() => {
    const allNodeIds = [
      ...pubQueue.items.map((q) => q.node_id).filter((id): id is string => !!id),
      ...readyArticles.map((c) => c.node_id),
    ];
    if (allNodeIds.length === 0) {
      setEffectiveDates(new Map());
      return;
    }
    let cancelled = false;
    fetchEffectivePublishDates(allNodeIds).then((m) => {
      if (!cancelled) setEffectiveDates(m);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pubQueue.items, readyArticles.length]);

  // Załaduj WP creds + schema_enabled dla domeny (po websiteId)
  useEffect(() => {
    if (!websiteId) {
      setWebsiteInfo({ creds: null, schemaEnabled: false, name: null });
      return;
    }
    let cancelled = false;
    fetchWebsiteForBackfill(websiteId).then((w) => {
      if (!cancelled) setWebsiteInfo(w);
    });
    return () => { cancelled = true; };
  }, [websiteId]);

  // Załaduj stan schemy (które opublikowane MAJĄ już schemę) — z schema_injection_logs.
  // Re-fetch po zakończeniu backfilla (schemaBackfill.summary).
  useEffect(() => {
    const ids = publishedKey ? publishedKey.split(",") : [];
    if (ids.length === 0) {
      setSchemaStatus(new Map());
      return;
    }
    let cancelled = false;
    fetchSchemaStatusMap(ids).then((m) => {
      if (!cancelled) setSchemaStatus(m);
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publishedKey, schemaBackfill.summary]);

  // Bulk backfill schemy (tylko brakujące, albo wszystkie gdy force)
  const handleBackfillSchema = useCallback(() => {
    if (!websiteInfo.creds || !websiteInfo.schemaEnabled || schemaBackfill.running) return;
    const items = backfillTargets.map((c) => ({
      nodeId: c.node_id,
      slug: nodeSlugMap.get(c.node_id) ?? null,
      wpPostId: (c as any).wp_post_id ?? null,
      label: (c as any).seo_title || nodeTitleMap.get(c.node_id) || c.node_id.slice(0, 8),
    }));
    if (items.length === 0) return;
    schemaBackfill.start(items, websiteInfo.creds).then(() => {
      toast({ title: "Schema", description: "Dogenerowanie zakończone — sprawdź podsumowanie." });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [websiteInfo, backfillTargets, schemaBackfill, nodeSlugMap, nodeTitleMap, toast]);

  // Pojedynczy wpis (per-wiersz)
  const handleBackfillOne = useCallback(
    (nodeId: string, wpPostId: number | null, label: string) => {
      if (!websiteInfo.creds || !websiteInfo.schemaEnabled || schemaBackfill.running) return;
      schemaBackfill.start(
        [{ nodeId, slug: nodeSlugMap.get(nodeId) ?? null, wpPostId, label }],
        websiteInfo.creds,
      );
    },
    [websiteInfo, schemaBackfill, nodeSlugMap],
  );

  const handleBackdateApplied = useCallback(async () => {
    const m = await fetchEffectivePublishDates(backdateScopeNodeIds);
    setEffectiveDates(m);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backdateScopeNodeIds.length]);

  const handleClearBackdated = useCallback(async () => {
    const withDates = backdateScopeNodeIds.filter((id) => effectiveDates.get(id));
    if (withDates.length === 0) return;
    if (!confirm(`Wyczyścić daty backdated dla ${withDates.length} wpisów?\n\n` +
                 `Wpisy wrócą do default flow (NOW przy publish).`)) return;
    await clearBackdatedDates(withDates);
    toast({
      title: "Daty wyczyszczone",
      description: `${withDates.length} wpisów wróciło do default flow.`,
    });
    handleBackdateApplied();
  }, [backdateScopeNodeIds, effectiveDates, toast, handleBackdateApplied]);

  // Generation queue handlers
  const handleQueueMap = useCallback(async () => {
    const count = await queueMap(mapId);
    toast({ title: "Dodano do kolejki", description: `${count} nodes dodanych do generowania.` });
    genQueue.refresh();
  }, [mapId, toast, genQueue]);

  const handleStartProcessing = useCallback(async () => {
    setProcessing(true);
    try {
      const result = await processQueue({}, () => {});
      toast({
        title: "Przetwarzanie zakonczone",
        description: `Wygenerowano: ${result.processed}, bledy: ${result.failed}, koszt: $${result.totalCost.toFixed(2)}`,
      });
    } catch (err) {
      toast({ title: "Blad", description: err instanceof Error ? err.message : "Nieznany blad", variant: "destructive" });
    } finally {
      setProcessing(false);
    }
  }, [toast]);

  // JEDEN PRZYCISK — queueMap + processQueue w jednym flow z bypass budget limits
  // (default config blokuje po 50 nodów / $25/day → tu lecimy bez limitu)
  const handleAutoBatch = useCallback(async () => {
    const queuedCount = await queueMap(mapId);
    const alreadyQueued = genQueue.queuedCount;
    const totalToProcess = queuedCount + alreadyQueued;

    if (totalToProcess === 0) {
      toast({
        title: "Brak nodow do przetworzenia",
        description: "Wszystkie nody sa juz gotowe lub w kolejce.",
      });
      return;
    }

    const estimatedCost = totalToProcess * 4; // $4 avg empirical
    const estimatedHours = Math.max(1, Math.round((totalToProcess * 12) / 60 / 3)); // ~12 min/node, concurrency=3

    if (!confirm(
      `Wygenerowac WSZYSTKO (${totalToProcess} nodow)?\n\n` +
      `Oczekiwany koszt: ~$${estimatedCost} (${totalToProcess} x $4 avg)\n` +
      `Oczekiwany czas: ~${estimatedHours}h (concurrency=3)\n\n` +
      `WAZNE:\n` +
      `* Karta przegladarki MUSI pozostac otwarta\n` +
      `* Komputer NIE moze usypiac (Power Plan: never sleep)\n` +
      `* Limity dzienne wylaczone (budgetMode: false)\n` +
      `* Error w jednym nodzie -> continues do nastepnego\n\n` +
      `Kontynuowac?`
    )) return;

    if (queuedCount > 0) {
      toast({ title: "Dodano do kolejki", description: `${queuedCount} nowych nodow.` });
      genQueue.refresh();
    }

    setProcessing(true);
    try {
      const result = await processQueue(
        {
          maxConcurrentGenerations: 3,
          maxDailyGenerations: 1000,
          maxDailyCost: 5000,
          budgetMode: false,
          autoPublish: false,
          priorityMode: "demand_first",
        },
        (progress) => {
          console.log(
            `[AutoBatch] ${progress.current}/${progress.total} ${progress.nodeTitle ?? ""} stage=${progress.stage ?? "?"}`,
          );
        },
      );

      toast({
        title: result.failed > 0 ? "Batch zakonczony (z bledami)" : "Batch zakonczony",
        description: `Wygenerowano: ${result.processed}, bledy: ${result.failed}, koszt: $${result.totalCost.toFixed(2)}`,
        variant: result.failed > totalToProcess / 4 ? "destructive" : "default",
      });
    } catch (err) {
      toast({
        title: "Blad batch",
        description: err instanceof Error ? err.message : "Unknown",
        variant: "destructive",
      });
    } finally {
      setProcessing(false);
      genQueue.refresh();
    }
  }, [mapId, toast, genQueue]);

  const handleRetryFailed = useCallback(async () => {
    const count = await retryFailed(mapId);
    toast({ title: "Retry", description: `${count} items reset.` });
    genQueue.refresh();
  }, [mapId, toast, genQueue]);

  // Reset stuck jobs: status='in_progress' + (no lock OR expired lock) + older than 30min
  // Typowy przypadek: tab zamkniety mid-generacji (pre-Phase 0 locks = NULL)
  // KILL SWITCH: drain BullMQ + UPDATE DB rows na cancelled (worker dokonczy aktualny job)
  const handleClearAll = useCallback(async () => {
    if (!confirm(
      "WYCZYSCIC CALA KOLEJKE?\n\n" +
      "To zatrzyma WSZYSTKIE oczekujace i in_progress joby:\n" +
      "  • generation_queue (CE + map_seeds/nodes/encje/eav/linking/audit)\n" +
      "  • publish_queue (WordPress publish)\n\n" +
      "Worker dokonczy job ktory wlasnie procesuje (nie da sie przerwac fetch w trakcie),\n" +
      "ale NIE wezmie nowych. BullMQ Redis tez zostanie pusty.\n\n" +
      "Kontynuowac?"
    )) return;
    try {
      const r = await clearQueue("all");
      const totalDrained = r.bullmq.generation + r.bullmq.publish;
      const totalCancelled = r.db.generation + r.db.publish;
      toast({
        title: "Kolejka wyczyszczona",
        description: `BullMQ drain: ${totalDrained} jobow, DB cancel: ${totalCancelled} rowsow. ` +
                     `Worker dokonczy aktualny job ale NIE wezmie nowych.`,
      });
      genQueue.refresh();
      pubQueue.refresh();
    } catch (err) {
      toast({
        title: "Blad clearQueue",
        description: err instanceof Error ? err.message : "Unknown",
        variant: "destructive",
      });
    }
  }, [toast, genQueue, pubQueue]);

  const handleResetStuck = useCallback(async () => {
    if (!confirm("Zresetowac stuck jobs?\n\nSpusci wszystkie in_progress starsze niz 30 min bez aktywnego locka.\n(Aktywne joby ktore wlasnie leca przez workera NIE beda ruszone.)")) return;
    try {
      const { data, error } = await (supabase as any).rpc("reset_stuck_generation_jobs", { p_older_than_minutes: 30 });
      if (error) throw error;
      const count = (data ?? []).length;
      toast({
        title: count > 0 ? `Wyczyszczono ${count} stuck jobs` : "Brak stuck jobs",
        description: count > 0 ? "Oznaczone jako failed. Badge zniknie po refresh." : "Nic do wyczyszczenia.",
      });
      genQueue.refresh();
    } catch (err) {
      toast({
        title: "Blad resetu",
        description: err instanceof Error ? err.message : "Unknown",
        variant: "destructive",
      });
    }
  }, [toast, genQueue]);

  const handlePublishAll = useCallback(async () => {
    if (!websiteId) return;
    const toPublish = readyArticles.filter(
      (c) => !pubQueue.items.some((q) => q.node_id === c.node_id && q.status !== "cancelled" && q.status !== "failed")
    );
    if (toPublish.length === 0) return;
    pubQueue.addToQueue(toPublish.map((c) => c.node_id), websiteId);
    toast({ title: "Dodano do kolejki", description: `${toPublish.length} artykulow zaplanowanych do publikacji.` });
  }, [websiteId, readyArticles, pubQueue, toast]);

  const queueLive = genQueue.isLive || pubQueue.isLive;

  return (
    <div className="space-y-8">

      {/* ============================================= */}
      {/* Live status (debug/info — subtle top-right)    */}
      {/* ============================================= */}
      <div className="flex justify-end -mb-4">
        <LiveIndicator isLive={queueLive} label="Kolejki" />
      </div>

      {/* ============================================= */}
      {/* IN-CONTENT IMAGES opt-in toggle + TODO note     */}
      {/* ============================================= */}
      <div className="rounded-lg border border-amber-500/40 bg-amber-50/30 dark:bg-amber-950/15 p-3 space-y-2">
        <div className="flex items-center justify-between gap-3">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={enableInContentImages}
              onChange={(e) => toggleEnableInContentImages(e.target.checked)}
              className="h-4 w-4 rounded border-amber-500/50 text-amber-600 focus:ring-amber-500"
            />
            <span className="text-sm font-medium">
              Włącz generowanie obrazków in-content (opt-in)
            </span>
            <span className={`text-xs ${enableInContentImages ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground"}`}>
              ({enableInContentImages ? "ON — 0-3 obrazki per artykuł" : "OFF — bez obrazków w treści (default)"})
            </span>
          </label>
        </div>
        <p className="text-[11px] text-amber-700 dark:text-amber-400 leading-relaxed">
          ⚠ <strong>TODO:</strong> obrazki obecnie linkują na <code className="font-mono text-[10px]">fal.media</code> (hotlink — brak upload do WP Media).
          Powód: CORS preflight WP REST /wp/v2/media + CSP <code className="font-mono text-[10px]">img-src 'self' data: https:</code> blokują client-side multipart upload.
          Trzeba migrować na server-side upload przez Supabase Edge Function lub worker (gdy wróci). Wtedy obrazki będą w bibliotece WP Media z WebP konwersją.
        </p>
      </div>

      {/* ============================================= */}
      {/* WORKER MODE WARNING — browser tab requirement   */}
      {/* ============================================= */}
      {!browserAlertDismissed && (
        <Alert className="border-amber-500/50 bg-amber-50/50 dark:bg-amber-950/20">
          <Monitor className="h-4 w-4" />
          <AlertTitle className="flex items-center justify-between">
            <span>Generowanie wymaga otwartej karty przegladarki</span>
            <button
              type="button"
              onClick={dismissBrowserAlert}
              className="text-muted-foreground hover:text-foreground transition"
              aria-label="Zamknij ostrzezenie"
            >
              <X className="h-4 w-4" />
            </button>
          </AlertTitle>
          <AlertDescription className="text-xs">
            Kolejka generowania artykulow i publikacji WordPress dziala w tej karcie przegladarki.
            Jesli zamkniesz tab, przetwarzanie zatrzyma sie do momentu ponownego otwarcia i klikniecia "Start".
            Tryb workera 24/7 (browser-independent) jest w przygotowaniu.
          </AlertDescription>
        </Alert>
      )}

      {/* ============================================= */}
      {/* INSTRUKCJA — jak dziala ta strona */}
      {/* ============================================= */}
      <div className="rounded-lg border bg-muted/30 p-4 space-y-2">
        <h3 className="text-sm font-semibold">Jak publikowac artykuly?</h3>
        <div className="grid grid-cols-3 gap-4 text-xs text-muted-foreground">
          <div className="flex gap-2">
            <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/20 text-primary flex items-center justify-center text-[10px] font-bold">1</span>
            <div>
              <p className="font-medium text-foreground">Wygeneruj obrazy</p>
              <p>Kliknij ikonke obrazu przy artykule lub "Generuj wszystkie" zeby FAL.AI stworzyl featured image.</p>
            </div>
          </div>
          <div className="flex gap-2">
            <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/20 text-primary flex items-center justify-center text-[10px] font-bold">2</span>
            <div>
              <p className="font-medium text-foreground">Dodaj do kolejki WP</p>
              <p>Kliknij "Publikuj" przy artykule lub "Publikuj wszystkie" — artykul trafi do kolejki z harmonogramem.</p>
            </div>
          </div>
          <div className="flex gap-2">
            <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/20 text-primary flex items-center justify-center text-[10px] font-bold">3</span>
            <div>
              <p className="font-medium text-foreground">Publikacja do WordPress</p>
              <p>System automatycznie uploaduje obraz i tworzy post jako draft w WP. Max 2 posty/dzien/domena.</p>
            </div>
          </div>
        </div>
      </div>

      {/* ============================================= */}
      {/* TABELA ARTYKULOW — glowny widok */}
      {/* ============================================= */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-medium flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4" />
            Artykuly
          </h3>
          <div className="flex items-center gap-2">
            {readyArticles.length > 0 && (
              <Badge variant="outline" className="text-xs bg-emerald-500/15 text-emerald-700 dark:text-emerald-400">
                {readyArticles.length} gotowych
              </Badge>
            )}
            {publishedArticles.length > 0 && (
              <Badge variant="outline" className="text-xs bg-green-500/15 text-green-700 dark:text-green-400">
                {publishedArticles.length} opublikowanych
              </Badge>
            )}
          </div>
        </div>

        {/* Bulk actions */}
        {readyArticles.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap">
            {nodesWithoutImage.length > 0 && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => imageGen.generateForMap(nodesWithoutImage.map((c) => c.node_id))}
                disabled={imageGen.loading}
              >
                <ImageIcon className="mr-1.5 h-3.5 w-3.5" />
                {imageGen.loading
                  ? `Generuje obrazy... ${imageGen.progress ? `(${imageGen.progress.current}/${imageGen.progress.total})` : ""}`
                  : `Generuj obrazy (${nodesWithoutImage.length})`}
              </Button>
            )}
            <Button
              size="sm"
              onClick={handlePublishAll}
              disabled={!websiteId || readyArticles.length === 0}
            >
              <Send className="mr-1.5 h-3.5 w-3.5" />
              Publikuj wszystkie ({readyArticles.length})
            </Button>
            {!websiteId && (
              <span className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                Ustaw website_id w ustawieniach mapy
              </span>
            )}
          </div>
        )}

        {/* Bulk: dogeneruj schemę do opublikowanych */}
        {publishedArticles.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap rounded-lg border bg-muted/10 p-2">
            <Button
              size="sm"
              variant="outline"
              onClick={handleBackfillSchema}
              disabled={!websiteInfo.creds || !websiteInfo.schemaEnabled || schemaBackfill.running || backfillN === 0}
            >
              {schemaBackfill.running ? (
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              ) : (
                <Braces className="mr-1.5 h-3.5 w-3.5" />
              )}
              Dogeneruj schemę ({backfillN})
            </Button>
            <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer select-none">
              <Checkbox
                checked={backfillForce}
                onCheckedChange={(v) => setBackfillForce(!!v)}
                disabled={schemaBackfill.running}
              />
              Odśwież też istniejące (force)
            </label>
            {!websiteInfo.schemaEnabled && (
              <span className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                Włącz schema.org dla domeny (zakładka Domeny) aby dogenerować
              </span>
            )}
            {schemaBackfill.running && (
              <span className="text-xs text-muted-foreground flex items-center gap-2">
                {schemaBackfill.progress.current}/{schemaBackfill.progress.total} ·{" "}
                <span className="text-emerald-600 dark:text-emerald-400">✓ {schemaBackfill.progress.ok}</span>
                <span>⏭ {schemaBackfill.progress.skipped}</span>
                {schemaBackfill.progress.failed > 0 && (
                  <span className="text-red-500">✗ {schemaBackfill.progress.failed}</span>
                )}
                <Button size="sm" variant="ghost" className="h-5 px-1 text-red-500" onClick={schemaBackfill.stop}>
                  <X className="h-3 w-3" /> stop
                </Button>
              </span>
            )}
            {!schemaBackfill.running && schemaBackfill.summary && (
              <span className="text-xs text-muted-foreground">
                Gotowe ({schemaBackfill.summary.durationSec}s): ✓ {schemaBackfill.summary.ok} · ⏭ {schemaBackfill.summary.skipped}
                {schemaBackfill.summary.failed > 0 && ` · ✗ ${schemaBackfill.summary.failed}`}
              </span>
            )}
          </div>
        )}

        {readyArticles.length === 0 && publishedArticles.length === 0 && (
          <div className="rounded-lg border border-dashed p-6 text-center">
            <p className="text-sm text-muted-foreground">
              Brak gotowych artykulow. Wygeneruj tresc w kroku "Tresc" — artykuly po QA pojawia sie tutaj.
            </p>
          </div>
        )}

        {(readyArticles.length > 0 || publishedArticles.length > 0) && (
          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[50px]">Obraz</TableHead>
                  <TableHead>Tytul artykulu</TableHead>
                  <TableHead className="w-[60px]">QA</TableHead>
                  <TableHead className="w-[100px]">Status</TableHead>
                  <TableHead className="w-[130px]">Akcja</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {readyArticles.map((c) => {
                  const title = (c as any).seo_title || nodeTitleMap.get(c.node_id) || c.node_id.slice(0, 12);
                  const inQueue = pubQueue.items.some((q) => q.node_id === c.node_id && q.status !== "cancelled" && q.status !== "failed");
                  return (
                    <TableRow key={c.node_id}>
                      <TableCell className="py-1.5">
                        {c.featured_image_url ? (
                          <a href={c.featured_image_url} target="_blank" rel="noopener noreferrer"
                             className="block w-10 h-7 rounded overflow-hidden hover:ring-2 ring-primary/40 transition-all">
                            <img src={c.featured_image_url} alt="" className="w-full h-full object-cover" />
                          </a>
                        ) : (
                          <Button
                            size="icon"
                            variant="outline"
                            className="h-7 w-10 rounded"
                            onClick={() => imageGen.generateForNode(c.node_id)}
                            disabled={imageGen.loading}
                            title="Kliknij zeby wygenerowac obraz (FAL.AI)"
                          >
                            <ImageLucide className="h-3.5 w-3.5 text-muted-foreground" />
                          </Button>
                        )}
                      </TableCell>
                      <TableCell className="text-xs py-1.5" title={title}>
                        <span className="line-clamp-1">{title}</span>
                      </TableCell>
                      <TableCell className="py-1.5">
                        <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${
                          c.qa_status === "approved" ? "bg-green-500/15 text-green-700 dark:text-green-400"
                          : c.qa_status === "needs_review" ? "bg-amber-500/15 text-amber-700 dark:text-amber-400"
                          : "bg-muted text-muted-foreground"
                        }`}>
                          {c.quality_score || "—"}
                        </span>
                      </TableCell>
                      <TableCell className="py-1.5">
                        {inQueue ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-amber-500/15 text-amber-700 dark:text-amber-400">
                            w kolejce
                          </span>
                        ) : c.qa_status === "needs_review" ||
                          c.qa_status === "failed" ? (
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span
                              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-amber-500/15 text-amber-700 dark:text-amber-400"
                              title={`qa_status=${c.qa_status} — gate publish wymaga 'approved'`}
                            >
                              wymaga zatwierdzenia
                            </span>
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-6 px-2 text-[11px] border-amber-500/40 text-amber-700 hover:bg-amber-500/10 dark:text-amber-300"
                              onClick={() =>
                                openQaOverride(
                                  c.node_id,
                                  title,
                                  c.quality_score ?? null,
                                )
                              }
                            >
                              <ShieldCheck className="h-3 w-3 mr-1" />
                              Zatwierdź
                            </Button>
                          </div>
                        ) : (c as { qa_override?: boolean }).qa_override ? (
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-violet-500/15 text-violet-700 dark:text-violet-400"
                            title="Zatwierdzone ręcznie pomimo QA (qa_override=true)"
                          >
                            <ShieldCheck className="h-3 w-3" />
                            gotowy (zatw. ręcznie)
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-emerald-500/15 text-emerald-700 dark:text-emerald-400">
                            gotowy
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="py-1.5">
                        {inQueue ? (
                          <span className="text-xs text-muted-foreground">zaplanowany</span>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-6 px-2 text-xs"
                            disabled={!websiteId}
                            onClick={() => {
                              if (!websiteId) return;
                              pubQueue.addToQueue([c.node_id], websiteId);
                              toast({ title: "Dodano", description: `"${title.slice(0, 40)}" dodany do kolejki WP.` });
                            }}
                          >
                            <Send className="mr-1 h-3 w-3" />
                            Publikuj
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {publishedArticles.map((c) => {
                  const title = (c as any).seo_title || nodeTitleMap.get(c.node_id) || c.node_id.slice(0, 12);
                  return (
                    <TableRow key={c.node_id} className="opacity-50">
                      <TableCell className="py-1.5">
                        {c.featured_image_url ? (
                          <img src={c.featured_image_url} alt="" className="w-10 h-7 rounded object-cover" />
                        ) : (
                          <div className="w-10 h-7 rounded bg-muted flex items-center justify-center">
                            <span className="text-[9px] text-muted-foreground">—</span>
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-xs py-1.5">
                        <span className="line-clamp-1">{title}</span>
                      </TableCell>
                      <TableCell className="py-1.5">
                        <span className="text-xs">{c.quality_score || "—"}</span>
                      </TableCell>
                      <TableCell className="py-1.5">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-green-500/15 text-green-700 dark:text-green-400">
                          opublikowany
                        </span>
                      </TableCell>
                      <TableCell className="py-1.5">
                        <div className="flex items-center gap-2">
                          {(c as any).wp_post_url ? (
                            <a href={(c as any).wp_post_url} target="_blank" rel="noopener noreferrer"
                               className="text-xs text-primary hover:underline flex items-center gap-1">
                              <ExternalLink className="h-3 w-3" /> Otworz w WP
                            </a>
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 px-1.5 text-xs text-muted-foreground hover:text-amber-500"
                            title="Cofnij publikację — post → draft w WP, DB reset"
                            onClick={() => setRevertDialog({ nodeId: c.node_id as string, title: (c as any).title || "Artykuł" })}
                          >
                            <Undo2 className="h-3 w-3 mr-1" />
                            Cofnij
                          </Button>
                          {websiteInfo.schemaEnabled && (
                            <span className="flex items-center gap-1">
                              {schemaStatus.has(c.node_id) ? (
                                <span
                                  className="text-[11px] text-emerald-600 dark:text-emerald-400"
                                  title={`Schema wstrzyknięta: ${new Date(schemaStatus.get(c.node_id) as string).toLocaleString("pl-PL")}`}
                                >
                                  schema ✓
                                </span>
                              ) : (
                                <span className="text-[11px] text-muted-foreground" title="Brak schemy w tym wpisie">
                                  schema: brak
                                </span>
                              )}
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-6 px-1.5 text-xs text-muted-foreground hover:text-primary"
                                disabled={schemaBackfill.running}
                                title={schemaStatus.has(c.node_id) ? "Odśwież schemę (force)" : "Dogeneruj schemę"}
                                onClick={() =>
                                  handleBackfillOne(
                                    c.node_id,
                                    (c as any).wp_post_id ?? null,
                                    (c as any).seo_title || nodeTitleMap.get(c.node_id) || c.node_id.slice(0, 8),
                                  )
                                }
                              >
                                <Braces className="h-3 w-3" />
                              </Button>
                            </span>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* ============================================= */}
      {/* KOLEJKA PUBLIKACJI — harmonogram WP */}
      {/* ============================================= */}
      {pubQueue.items.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Kolejka publikacji WordPress
            </h3>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-xs">
                {pubQueue.status.scheduled} zaplanowane
              </Badge>
              <Badge variant="outline" className="text-xs bg-green-500/15 text-green-700 dark:text-green-400">
                {pubQueue.status.published} opublikowane
              </Badge>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <Button
              size="sm"
              onClick={() => pubQueue.publish(true)}
              disabled={pubQueue.loading || (pubQueue.status.scheduled + pubQueue.status.queued) === 0}
              title="Procesuje WSZYSTKIE pending jobs (queued + scheduled). forceAll=true fast-forwards scheduled_at."
            >
              <Play className="mr-1.5 h-3.5 w-3.5" />
              Publikuj wszystkie teraz ({pubQueue.status.scheduled + pubQueue.status.queued})
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setBackdateDialogOpen(true)}
              disabled={backdateScopeNodeIds.length === 0}
              title="Rozłóż daty publikacji na X dni wstecz (schema.org + WP post.date_gmt)"
            >
              <Calendar className="mr-1.5 h-3.5 w-3.5" />
              Zaplanuj daty wstecz ({backdateScopeNodeIds.length})
            </Button>
            {backdateScopeNodeIds.some((id) => effectiveDates.get(id)) && (
              <Button
                size="sm"
                variant="ghost"
                onClick={handleClearBackdated}
                className="text-muted-foreground"
                title="Wyczyść backdated daty — wrót do default flow (NOW)"
              >
                <X className="mr-1 h-3 w-3" />
                Wyczyść daty
              </Button>
            )}
            {(() => {
              const terminalCount = pubQueue.items.filter(
                (q) => q.status === "cancelled" || q.status === "failed"
              ).length;
              if (terminalCount === 0) return null;
              return (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    if (!confirm(
                      `Usunąć ${terminalCount} rekordów (cancelled + failed) z historii kolejki?\n\n` +
                      `Nie tknie published (live posts) ani aktywnych jobs.`
                    )) return;
                    pubQueue.clearHistory();
                  }}
                  className="text-muted-foreground hover:text-red-500"
                  title="Usuń cofnięte i failed rekordy z bazy"
                >
                  <Trash2 className="mr-1 h-3 w-3" />
                  Wyczyść historię ({terminalCount})
                </Button>
              );
            })()}
          </div>

          <div className="border rounded-lg max-h-[700px] overflow-y-auto">
            <Table>
              <TableHeader className="sticky top-0 bg-background z-10 shadow-sm">
                <TableRow>
                  <TableHead>Artykul</TableHead>
                  <TableHead className="w-[140px]">Zaplanowano na</TableHead>
                  <TableHead className="w-[160px]">Data publikacji</TableHead>
                  <TableHead className="w-[100px]">Status</TableHead>
                  <TableHead className="w-[60px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pubQueue.items.map((item) => {
                  const title = item.node_id
                    ? (nodeTitleMap.get(item.node_id) || item.node_id.slice(0, 12))
                    : "—";
                  const effectiveDate = item.node_id ? effectiveDates.get(item.node_id) : null;
                  return (
                    <TableRow key={item.id}>
                      <TableCell className="text-xs">
                        <span className="line-clamp-1">{title}</span>
                      </TableCell>
                      <TableCell className="text-xs">
                        {item.scheduled_at
                          ? new Date(item.scheduled_at).toLocaleString("pl-PL", {
                              day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
                            })
                          : "—"}
                      </TableCell>
                      <TableCell className="text-xs">
                        {effectiveDate ? (
                          <span
                            className="font-mono text-emerald-600 dark:text-emerald-400"
                            title="Backdated — trafi do schema.org datePublished + WP post.date_gmt"
                          >
                            {effectiveDate.toLocaleString("pl-PL", {
                              day: "2-digit", month: "2-digit", year: "2-digit",
                              hour: "2-digit", minute: "2-digit",
                            })}
                          </span>
                        ) : (
                          <span className="text-muted-foreground italic">NOW przy publish</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs ${STATUS_COLORS[item.status] || ""}`}>
                          {item.status}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          {item.status === "scheduled" && (
                            <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => pubQueue.cancel(item.id)}>
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          )}
                          {item.wp_post_url && (
                            <a href={item.wp_post_url} target="_blank" rel="noopener noreferrer"
                               className="text-xs text-primary hover:underline">WP</a>
                          )}
                          {item.status === "published" && item.node_id && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-6 px-1.5 text-xs text-muted-foreground hover:text-amber-500"
                              title="Cofnij publikację — post → draft w WP, DB reset (mozna ponownie generowac i publikowac)"
                              onClick={() => setRevertDialog({
                                nodeId: item.node_id as string,
                                title: (nodeTitleMap.get(item.node_id as string) || "Artykuł"),
                              })}
                            >
                              <Undo2 className="h-3 w-3 mr-1" />
                              Cofnij
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {/* ============================================= */}
      {/* KOLEJKA GENEROWANIA — batch content engine */}
      {/* ============================================= */}
      <div className="space-y-3 border-t pt-6">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-medium flex items-center gap-2">
            <Rocket className="h-4 w-4" />
            Kolejka generowania artykulow
          </h3>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-xs">{genQueue.queuedCount} oczekuje</Badge>
            <Badge variant="outline" className="text-xs">{genQueue.inProgressCount} w toku</Badge>
            <Badge variant="outline" className="text-xs bg-green-500/15 text-green-700 dark:text-green-400">
              {genQueue.completedCount} gotowe
            </Badge>
            {genQueue.failedCount > 0 && (
              <Badge variant="destructive" className="text-xs">{genQueue.failedCount} bledy</Badge>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            size="sm"
            onClick={handleAutoBatch}
            disabled={processing}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            title="Dodaje wszystkie nody mapy do kolejki + odpala przetwarzanie z wylaczonym limitem dziennym (jeden klik dla pelnego batcha)"
          >
            <Rocket className="mr-1 h-3.5 w-3.5" />
            {processing ? "Generuje..." : "Wygeneruj WSZYSTKO + Start"}
          </Button>
          <Button size="sm" variant="outline" onClick={handleQueueMap} disabled={processing}>
            Dodaj cala mape
          </Button>
          <Button size="sm" variant="outline" onClick={handleStartProcessing}
                  disabled={processing || genQueue.queuedCount === 0}>
            <Play className="mr-1 h-3.5 w-3.5" />
            {processing ? "Generuje..." : "Start"}
          </Button>
          {processing && (
            <Button size="sm" variant="outline" onClick={pauseQueue}>
              <Pause className="mr-1 h-3.5 w-3.5" /> Pauza
            </Button>
          )}
          {genQueue.failedCount > 0 && (
            <Button size="sm" variant="outline" onClick={handleRetryFailed}>
              <RotateCcw className="mr-1 h-3.5 w-3.5" /> Retry ({genQueue.failedCount})
            </Button>
          )}
          {genQueue.inProgressCount > 0 && (
            <Button
              size="sm"
              variant="ghost"
              onClick={handleResetStuck}
              className="text-amber-600 hover:text-amber-700"
              title="Zresetuj stuck jobs (in_progress > 30min bez aktywnego locka)"
            >
              <Wrench className="mr-1 h-3.5 w-3.5" /> Reset stuck
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            onClick={handleClearAll}
            className="text-red-600 hover:text-red-700 hover:bg-red-500/10 border border-red-500/30"
            title="KILL SWITCH: drain BullMQ + cancel wszystkich queued/in_progress (gen + publish)"
          >
            <Trash2 className="mr-1 h-3.5 w-3.5" /> Wyczysc kolejke (KILL)
          </Button>
        </div>

        {genQueue.items.length > 0 && (
          <div className="max-h-96 overflow-auto border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[28px]"></TableHead>
                  <TableHead className="w-[40px]">#</TableHead>
                  <TableHead>Artykul</TableHead>
                  <TableHead className="w-[60px]">Prior.</TableHead>
                  <TableHead className="w-[100px]">Status</TableHead>
                  <TableHead className="w-[80px]">Koszt</TableHead>
                  <TableHead className="w-[60px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {genQueue.items.slice(0, 20).map((item, idx) => {
                  const progressLog = ((item.config as any)?.progress_log ?? []) as Array<{ t: string; stage: string; label: string }>;
                  const hasLog = progressLog.length > 0;
                  const expanded = expandedQueueRows.has(item.id);
                  const lastEntries = progressLog.slice(-20);
                  // map_* jobs (seeds/nodes/nodes_batch) maja node_id=null — graj na step+map_id zamiast crashowac
                  const nodeTitle = item.node_id
                    ? (nodeTitleMap.get(item.node_id) || item.node_id.slice(0, 8))
                    : (item.step ? `${item.step.replace(/^map_/, "")} (mapa)` : "—");
                  return (
                    <React.Fragment key={item.id}>
                      <TableRow className={hasLog ? "cursor-pointer hover:bg-muted/40" : ""} onClick={hasLog ? () => toggleExpandQueueRow(item.id) : undefined}>
                        <TableCell className="text-muted-foreground">
                          {hasLog && (expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />)}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">{idx + 1}</TableCell>
                        <TableCell className="text-xs truncate max-w-[200px]">{nodeTitle}</TableCell>
                        <TableCell className="text-xs">{item.priority}</TableCell>
                        <TableCell>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs ${STATUS_COLORS[item.status] || ""}`}>
                            {item.status}
                          </span>
                        </TableCell>
                        <TableCell className="text-xs">
                          {item.result ? `$${((item.result as any)?.cost?.total || 0).toFixed(3)}` : "—"}
                        </TableCell>
                        <TableCell>
                          {hasLog && (
                            <Button
                              size="icon" variant="ghost" className="h-6 w-6"
                              onClick={(e) => { e.stopPropagation(); setProgressModalQueueId(item.id); }}
                              title="Pelen log"
                            >
                              <Terminal className="h-3 w-3" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                      {expanded && hasLog && (
                        <TableRow className="border-0 bg-muted/30">
                          <TableCell colSpan={7} className="py-2 px-3">
                            <div className="font-mono text-[11px] space-y-0.5 max-h-40 overflow-y-auto">
                              {lastEntries.map((e, i) => (
                                <div key={i} className="flex gap-2">
                                  <span className="text-muted-foreground/60 shrink-0">
                                    {new Date(e.t).toLocaleTimeString("pl-PL", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
                                  </span>
                                  <span className="shrink-0 text-blue-600 dark:text-blue-400">[{e.stage}]</span>
                                  <span className="text-foreground">{e.label}</span>
                                </div>
                              ))}
                              {progressLog.length > 20 && (
                                <p className="text-muted-foreground/60 italic mt-1">
                                  Pokazano ostatnie 20 z {progressLog.length}. Klik <Terminal className="inline h-3 w-3" /> dla pelnego logu.
                                </p>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Progress log modal */}
      {progressModalQueueId && (() => {
        const item = genQueue.items.find((i) => i.id === progressModalQueueId);
        if (!item) return null;
        const entries = ((item.config as any)?.progress_log ?? []) as Array<{ t: string; stage: string; label: string }>;
        const title = item.node_id
          ? (nodeTitleMap.get(item.node_id) || item.node_id.slice(0, 8))
          : (item.step ? `${item.step.replace(/^map_/, "")} (mapa)` : "—");
        return (
          <ProgressLogModal
            open={true}
            onOpenChange={(open) => { if (!open) setProgressModalQueueId(null); }}
            nodeTitle={title}
            nodeId={item.node_id ?? undefined}
            entries={entries}
          />
        );
      })()}

      {/* ============================================= */}
      {/* ZAPLANUJ DATY WSTECZ — Dialog */}
      {/* ============================================= */}
      <BackdateScheduleDialog
        open={backdateDialogOpen}
        onOpenChange={setBackdateDialogOpen}
        nodeIds={backdateScopeNodeIds}
        onApplied={handleBackdateApplied}
      />

      {/* ============================================= */}
      {/* COFNIJ PUBLIKACJĘ — Dialog */}
      {/* ============================================= */}
      <AlertDialog open={!!revertDialog} onOpenChange={(o) => { if (!o) setRevertDialog(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Cofnąć publikację?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm">
                <p>
                  Artykuł <strong>"{revertDialog?.title}"</strong> zostanie cofnięty z publikacji:
                </p>
                <ul className="list-disc list-inside space-y-1 text-xs text-muted-foreground">
                  <li>WordPress: post → status <strong>draft</strong> (ukryty, ale trwały — możesz przywrócić z WP Admin)</li>
                  <li>DB: <code>engine_stage=ready</code>, <code>published_at/wp_post_id/wp_post_url</code> = NULL</li>
                  <li>Kolejka publikacji: wpisy <code>published</code> → <code>cancelled</code> (audit trail)</li>
                  <li>Node status: <code>published</code> → <code>draft</code></li>
                </ul>
                <div className="flex items-start gap-2 rounded-md bg-muted/40 p-2.5 mt-3">
                  <Checkbox
                    id="revert-clear-image"
                    checked={revertClearImage}
                    onCheckedChange={(v) => setRevertClearImage(v === true)}
                    className="mt-0.5"
                  />
                  <label htmlFor="revert-clear-image" className="text-xs leading-snug cursor-pointer flex-1">
                    <strong>Usuń też featured image URL</strong> — wymusi regenerację obrazka przy następnej publikacji
                    (zalecane jeśli chcesz "pełny reset" i nowy obrazek).
                  </label>
                </div>
                <p className="text-[11px] text-muted-foreground italic">
                  Schema audit logs zostają nietknięte (historia publikacji). Node będzie gotowy do ponownego kliknięcia Publikuj.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={reverting}>Anuluj</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleRevert(); }}
              disabled={reverting}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {reverting ? "Cofam..." : "Tak, cofnij publikację"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* QA Override dialog — force-approve publish dla artykułu z qa_status=needs_review/failed */}
      <AlertDialog
        open={qaOverrideTarget !== null}
        onOpenChange={(open) => {
          if (!open && !qaOverrideLoading) {
            setQaOverrideTarget(null);
            setQaOverrideReason("");
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-amber-500" />
              Zatwierdzić mimo niższego QA?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 pt-1">
                <div className="text-sm">
                  Artykuł:{" "}
                  <span className="font-medium text-foreground">
                    {qaOverrideTarget?.title.slice(0, 80)}
                    {qaOverrideTarget && qaOverrideTarget.title.length > 80
                      ? "…"
                      : ""}
                  </span>
                </div>
                {qaOverrideTarget?.qaScore != null && (
                  <div className="text-sm">
                    QA score:{" "}
                    <span
                      className={
                        qaOverrideTarget.qaScore >= 70
                          ? "font-bold text-emerald-500"
                          : qaOverrideTarget.qaScore >= 40
                            ? "font-bold text-amber-500"
                            : "font-bold text-rose-500"
                      }
                    >
                      {qaOverrideTarget.qaScore}/100
                    </span>
                  </div>
                )}
                <div className="text-sm text-muted-foreground">
                  Standardowo publish wymaga{" "}
                  <code className="px-1 py-0.5 rounded bg-muted text-[11px]">
                    qa_status = approved
                  </code>
                  . Audit oznaczył ten artykuł jako{" "}
                  <code className="px-1 py-0.5 rounded bg-muted text-[11px]">
                    needs_review
                  </code>{" "}
                  (np. brak źródeł, niska entity coverage, schema flag).
                  Override zapisze audit trail w DB (kto + kiedy + powód).
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-medium text-foreground">
                    Powód override (opcjonalnie):
                  </label>
                  <Textarea
                    value={qaOverrideReason}
                    onChange={(e) => setQaOverrideReason(e.target.value)}
                    placeholder="np. schema generowana przez WP plugin po publish — issue audytowy false-positive"
                    rows={3}
                    className="text-sm"
                    disabled={qaOverrideLoading}
                  />
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={qaOverrideLoading}>
              Anuluj
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleQaOverride();
              }}
              disabled={qaOverrideLoading}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {qaOverrideLoading
                ? "Zatwierdzam…"
                : "Tak, zatwierdź i publikuj"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
